import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { formatDate } from "@/lib/format";

const EMPTY = {
  name: "", email: "", phone: "",
  address_line1: "", address_line2: "", postal_code: "", city: "", country: "Sweden",
  org_number: "", vat_number: "", notes: "",
};

export default function Customers() {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get("/customers");
      setList(data);
    } catch (e) {
      toast.error("Failed to load customers");
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY, ...c });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => payload[k] === "" && (payload[k] = null));
      payload.name = form.name; // name required
      if (!payload.country) payload.country = "Sweden";
      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer added");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    }
  };

  const del = async (c) => {
    if (!confirm(`Delete customer "${c.name}"?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <AdminLayout title="Customers">
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-[color:var(--ivory-muted)]">{list.length} customer{list.length !== 1 ? "s" : ""}</div>
        <button onClick={openCreate} className="pill pill-primary inline-flex items-center gap-2 text-sm" data-testid="add-customer-button">
          <Plus className="w-4 h-4" /> Add customer
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="bg-white/[0.03] text-[color:var(--ivory-muted)] text-xs uppercase tracking-[0.15em]">
            <tr>
              <Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>City</Th><Th>Org no.</Th><Th>Added</Th><Th />
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`customer-row-${c.id}`}>
                <Td className="font-medium">{c.name}</Td>
                <Td>{c.email || "—"}</Td>
                <Td>{c.phone || "—"}</Td>
                <Td>{c.city || "—"}</Td>
                <Td>{c.org_number || "—"}</Td>
                <Td>{formatDate(c.created_at)}</Td>
                <Td className="text-right">
                  <button onClick={() => openEdit(c)} className="p-2 hover:text-white text-[color:var(--ivory-muted)]" data-testid={`edit-customer-${c.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(c)} className="p-2 hover:text-[color:var(--bd-red-bright)] text-[color:var(--ivory-muted)]" data-testid={`delete-customer-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </Td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan="7" className="p-12 text-center text-[color:var(--ivory-muted)]">No customers yet. Add your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <Modal onClose={() => setShowForm(false)} title={editing ? "Edit customer" : "Add customer"}>
          <form onSubmit={save} className="space-y-4" data-testid="customer-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <FieldText label="Name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <FieldText type="email" label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <FieldText label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <FieldText label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
              <FieldText label="Address line 1" value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} />
              <FieldText label="Address line 2" value={form.address_line2} onChange={(v) => setForm({ ...form, address_line2: v })} />
              <FieldText label="Postal code" value={form.postal_code} onChange={(v) => setForm({ ...form, postal_code: v })} />
              <FieldText label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              <FieldText label="Organisation number" value={form.org_number} onChange={(v) => setForm({ ...form, org_number: v })} />
              <FieldText label="VAT number" value={form.vat_number} onChange={(v) => setForm({ ...form, vat_number: v })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2 block">Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="email-input pl-4 py-3" style={{ borderRadius: 12, resize: "vertical" }} />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="pill border border-white/15 text-white">Cancel</button>
              <button type="submit" className="pill pill-primary" data-testid="customer-save">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </AdminLayout>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 text-left font-semibold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;

export function FieldText({ label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2 block">{label}</label>
      <input type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="email-input pl-4" />
    </div>
  );
}

export function Modal({ children, onClose, title, wide }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-[#0E1216] border border-white/10 rounded-2xl w-full ${wide ? "max-w-4xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto`} data-testid="modal">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0E1216] z-10">
          <h3 className="font-display text-xl">{title}</h3>
          <button onClick={onClose} className="text-[color:var(--ivory-muted)] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
